<!-- Выбирает эмодзи категории расходов с поиском и группировкой. -->
<template>
  <base-select
    :model-value="modelValue"
    :options="emojiOptions"
    :placeholder="placeholder"
    :invalid="invalid"
    :disabled="disabled"
    :clearable="false"
    custom-dropdown
    @update:modelValue="handleUpdate"
  >
    <template
      #selected-value="{
        selectedOption,
        placeholder: selectPlaceholder,
      }"
    >
      <span v-if="selectedOption" class="emoji-selected-value">
        <span class="emoji-selected-value__icon">
          {{ selectedOption.value }}
        </span>
        <span class="emoji-selected-value__label">
          {{ selectedOption.name }}
        </span>
      </span>

      <span v-else class="emoji-selected-placeholder">
        {{ selectPlaceholder }}
      </span>
    </template>

    <template
      #dropdown-content="{
        searchQuery,
        setSearchQuery,
        selectOption,
      }"
    >
      <div class="emoji-picker-search">
        <base-input
          :model-value="searchQuery"
          placeholder="Поиск эмодзи..."
          type="text"
          :clearable="true"
          @update:modelValue="setSearchQuery"
        />
      </div>

      <div class="emoji-categories">
        <button
          v-for="cat in emojiCategories"
          :key="cat.id"
          type="button"
          class="emoji-cat-btn"
          :class="{ active: activeEmojiCategory === cat.id }"
          :disabled="disabled"
          :title="cat.label"
          @click="activeEmojiCategory = cat.id"
        >
          {{ cat.icon }}
        </button>
      </div>

      <div class="emoji-grid">
        <button
          v-for="emoji in filteredEmojis(searchQuery)"
          :key="emoji.char"
          type="button"
          class="emoji-btn"
          :class="{ selected: modelValue === emoji.char }"
          :disabled="disabled"
          :title="emoji.name"
          @click="handleSelectEmoji(emoji, selectOption)"
        >
          {{ emoji.char }}
        </button>

        <span
          v-if="filteredEmojis(searchQuery).length === 0"
          class="emoji-empty"
        >
          Ничего не найдено
        </span>
      </div>
    </template>
  </base-select>
</template>

<script>
import { EXPENSE_CATEGORY_EMOJIS } from "@/config/expenseCategoryEmojis";

export default {
  name: "EmojiSelect",

  props: {
    modelValue: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "Выберите эмодзи",
    },
    invalid: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["update:modelValue", "change"],

  data() {
    return {
      activeEmojiCategory: "transport",
      emojiCategories: EXPENSE_CATEGORY_EMOJIS,
    };
  },

  computed: {
    emojiOptions() {
      return this.emojiCategories.flatMap((category) =>
        category.emojis.map((emoji) => ({
          value: emoji.char,
          label: `${emoji.char} ${emoji.name}`,
          name: emoji.name,
          category: category.id,
        }))
      );
    },
  },

  methods: {
    handleUpdate(value) {
      this.$emit("update:modelValue", value);
      this.$emit("change", value);
    },

    handleSelectEmoji(emoji, selectOption) {
      selectOption({
        value: emoji.char,
        label: `${emoji.char} ${emoji.name}`,
        name: emoji.name,
        category: this.activeEmojiCategory,
      });
    },

    filteredEmojis(searchQuery) {
      const query = String(searchQuery || "")
        .trim()
        .toLowerCase();

      if (query) {
        return this.emojiCategories.flatMap((category) =>
          category.emojis.filter((emoji) =>
            emoji.name.toLowerCase().includes(query)
          )
        );
      }

      const category = this.emojiCategories.find(
        (item) => item.id === this.activeEmojiCategory
      );

      return category ? category.emojis : [];
    },

    setInitialCategory() {
      if (!this.modelValue) return;

      const foundCategory = this.emojiCategories.find((category) =>
        category.emojis.some(
          (emoji) => emoji.char === this.modelValue
        )
      );

      if (foundCategory) {
        this.activeEmojiCategory = foundCategory.id;
      }
    },
  },

  watch: {
    modelValue() {
      this.setInitialCategory();
    },
  },

  created() {
    this.setInitialCategory();
  },
};
</script>

<style scoped>
.emoji-selected-value {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
  min-height: 28px;
  overflow: visible;
}

.emoji-selected-value__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 24px;
  width: 24px;
  min-width: 24px;
  height: 28px;
  margin: -2px 0;
  overflow: visible;
  font-size: 20px;
  line-height: 1.4;
}

.emoji-selected-value__label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.emoji-selected-placeholder {
  color: #999;
  opacity: 0.7;
}

.emoji-picker-search {
  padding: 8px 8px 0;
}

.emoji-categories {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  padding: 6px 8px;
  border-bottom: 1px solid var(--color-grey);
}

.emoji-cat-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  background: none;
  border: 1px solid transparent;
  border-radius: 5px;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  box-sizing: border-box;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.emoji-cat-btn:hover:not(:disabled) {
  background: #f5f5f5;
}

.emoji-cat-btn.active {
  background: #e8edff;
  border-color: var(--color-blue);
}

.emoji-cat-btn:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.emoji-grid {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 2px;
  max-height: 208px;
  padding: 8px;
  overflow-y: auto;
}

.emoji-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1;
  min-width: 0;
  min-height: 32px;
  padding: 2px;
  background: none;
  border: 1px solid transparent;
  border-radius: 5px;
  font-size: 20px;
  line-height: 1.4;
  cursor: pointer;
  text-align: center;
  box-sizing: border-box;
  overflow: visible;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.emoji-btn:hover:not(:disabled) {
  background: #f5f5f5;
}

.emoji-btn.selected {
  background: #e8edff;
  border-color: var(--color-blue);
}

.emoji-btn:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.emoji-empty {
  grid-column: 1 / -1;
  padding: 10px 0;
  color: #999;
  font-size: 13px;
  font-style: italic;
  text-align: center;
}

@media (max-width: 600px) {
  .emoji-grid {
    grid-template-columns: repeat(6, 1fr);
  }
}
</style>
